import { useState } from "react";
import { Icon, Popup, TableCell, TableRow } from "semantic-ui-react";
import { Card, DataTable } from "../../ui";
import { colors } from "../../../style";

const style = {
  card: {
    padding: "18px 20px 16px",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "14px",
  },
  title: {
    color: colors.text,
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
  },
  subtitle: {
    color: colors.textLight,
    fontSize: "12.5px",
    marginTop: "3px",
  },
  toggle: {
    cursor: "pointer",
    color: colors.textFaint,
    background: "none",
    border: "none",
    padding: "2px",
  },
  tableWrap: {
    maxHeight: "100%",
    overflowY: "auto",
  },
};

/**
 * Card chrome shared by every dashboard chart. `table` ({ columns, rows }) is
 * the accessible twin of the chart, shown via the header toggle.
 */
function ChartCard({
  title,
  subtitle,
  legend,
  table,
  height,
  style: extra,
  children,
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    <Card as="section" style={{ ...style.card, ...extra }}>
      <div style={style.header}>
        <div>
          <h3 style={style.title}>{title}</h3>
          {subtitle && <div style={style.subtitle}>{subtitle}</div>}
        </div>
        {table && (
          <Popup
            inverted
            size="mini"
            content={showTable ? "Show chart" : "Show data table"}
            trigger={
              <button
                type="button"
                style={style.toggle}
                aria-label={showTable ? "Show chart" : "Show data table"}
                onClick={() => setShowTable(!showTable)}
              >
                <Icon name={showTable ? "chart bar" : "table"} />
              </button>
            }
          />
        )}
      </div>
      {legend && !showTable && legend}
      <div style={{ height, ...(showTable ? style.tableWrap : {}) }}>
        {showTable ? <ChartTable {...table} /> : children}
      </div>
    </Card>
  );
}

function ChartTable({ columns, rows }) {
  return (
    <DataTable
      bare
      compact
      unstackable
      size="small"
      columns={columns.map((c) => ({ key: c, label: c }))}
    >
      {rows.map((row, i) => (
        <TableRow key={i}>
          {row.map((cell, j) => (
            <TableCell key={j}>{cell}</TableCell>
          ))}
        </TableRow>
      ))}
    </DataTable>
  );
}

export function Legend({ items }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "16px",
        marginBottom: "12px",
      }}
    >
      {items.map((item) => (
        <span
          key={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            color: colors.textMuted,
            fontSize: "12.5px",
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "3px",
              background: item.color,
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export default ChartCard;
